import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useItemListContext } from './ItemListProvider';
import { useAppDispatch } from '@/store/hooks';
import { updateResourceProjectPreviewStatus } from '../../apiServices/resourceApi';
import { updateItemPreview } from '../../app/features/project/projectSlice';
import type { NormalizedItem } from './types';

interface PreviewToggleProps {
  item: NormalizedItem;
}

/**
 * Eye button that shows whether a project file is in the project's main (work) view, and
 * toggles it. Used by both the card and the list view of project files.
 */
export function PreviewToggle({ item }: PreviewToggleProps) {
  const { updateItem } = useItemListContext();
  const reduxDispatch = useAppDispatch();
  const [toggling, setToggling] = useState(false);

  const handleToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (toggling) return;

    // Optimistic: flip immediately
    const newPreview = !item.preview;
    updateItem(item.id, { preview: newPreview });
    reduxDispatch(updateItemPreview(item));
    setToggling(true);

    try {
      await updateResourceProjectPreviewStatus(
        item.id,
        item.name || item.title,
        newPreview,
      );
    } catch {
      // Revert on failure
      updateItem(item.id, { preview: item.preview });
      reduxDispatch(updateItemPreview({ ...item, preview: newPreview }));
    } finally {
      setToggling(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={cn(
        'shrink-0 rounded p-1 transition-colors',
        item.preview
          ? 'text-primary hover:bg-primary-muted'
          : 'text-muted-foreground hover:bg-secondary hover:text-muted-foreground',
      )}
      title={item.preview ? 'Visible in main view (click to hide)' : 'Hidden from main view (click to show)'}
      aria-label={item.preview ? 'Hide from main view' : 'Show in main view'}
      aria-pressed={!!item.preview}
    >
      {item.preview ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
    </button>
  );
}
